import { startTransition, type Dispatch } from 'react'
import { toast } from 'sonner'
import { getStoredApiKey } from '@/lib/account/auth'
import { reencryptAndUpdateViaApiKey } from '@/lib/shares/load'
import type { EditorState } from '../state'
import type { EditorAction } from '../state'

interface UseShareUpdateOptions {
  setOptimisticOriginalContent: (content: string) => void
}

/**
 * Handles updating modifiable shares with optimistic UI.
 * Re-encrypts content using the same CEK via the API key and PUTs the update.
 */
export function useShareUpdate(
  state: EditorState,
  dispatch: Dispatch<EditorAction>,
  options: UseShareUpdateOptions,
) {
  const { setOptimisticOriginalContent } = options

  const handleUpdateShare = () => {
    if (!state.viewingShareId || !state.viewingShareMeta?.isModifiable || !state.viewingShareMeta.managementToken) {
      toast.error('Cannot update this share')
      return
    }

    if (!state.content.trim()) {
      toast.error('Content cannot be empty')
      return
    }

    const contentToSave = state.content
    const shareId = state.viewingShareId
    const managementToken = state.viewingShareMeta.managementToken

    startTransition(async () => {
      setOptimisticOriginalContent(contentToSave)
      dispatch({ type: 'SET_LOADING', operation: 'updateShare' })

      try {
        const apiKey = getStoredApiKey()
        if (!apiKey) {
          toast.error('API key required to update share')
          return
        }

        await reencryptAndUpdateViaApiKey(shareId, managementToken, contentToSave, apiKey)

        dispatch({ type: 'SET_ORIGINAL_CONTENT', originalContent: contentToSave })
        toast.success('Share updated successfully')
      } catch (error) {
        console.error('Failed to update share:', error)
        toast.error(error instanceof Error ? error.message : 'Failed to update share')
      } finally {
        dispatch({ type: 'SET_LOADING', operation: null })
      }
    })
  }

  return { handleUpdateShare }
}
